import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';

import * as d3 from 'd3';
import { GameRecord } from 'diwimamodelweb/lib';
import { FilterService } from '../../cockpit/statistics/filter.service';

export class DonutChartDatum {
  legend: string;
  species: string;
  count: number;
  records: GameRecord[]
}

export const toTableData = (records: GameRecord[]): any[] => {
  if (!records) return [] 
    let tableData: any[] = []
    // column species
    const speciesSet = new Set<string>()
    records.forEach((record) => {
      speciesSet.add(record.wildart)
    })
    speciesSet.forEach((species) => {
      let count = 0
      let _records = []
      records.forEach(el => {
        if (el.wildart == species) {
          count++
          _records.push(el)
        }
      })
      tableData.push({species, count, records: _records})
      // records = []
    })
    tableData.sort((a, b) => a.count > b.count ? -1 : 1)  
    return tableData
}

@Component({
  selector: 'app-donut-chart',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.css']
})
export class DonutChartComponent implements OnInit, OnChanges {

  @Input() data: GameRecord[];
  @Output() clickedSlice = new EventEmitter<any>()

  hostElement; // Native element hosting the SVG container
  svg; // Top level SVG element
  g; // SVG Group element
  arc; // D3 Arc generator
  innerRadius; // Inner radius of donut chart
  radius; // Outer radius of donut chart
  slices; // Donut chart slice elements
  labels; // SVG data label elements
  totalLabel; // SVG label for total
  rawData; // Raw chart values array
  total: number; // Total of chart values
  colorScale; // D3 color provider
  pieData: any; // Arc segment parameters for current data set
  pieDataPrevious: any; // Arc segment parameters for previous data set - used for transitions
  colors = d3.scaleOrdinal(d3.schemeCategory10);

  tableData: any

  // Pie function - transforms raw data to arc segment parameters
  pie = d3.pie()
    // .startAngle(-0.5 * Math.PI)
    // .endAngle(0.5 * Math.PI)
    .sort(null)
    .value((d: number) => d);

  constructor(private filterService: FilterService, private elRef: ElementRef) {
    this.hostElement = this.elRef.nativeElement;
  }

  ngOnInit(): void {

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.svg = undefined
      const data = toTableData(changes.data.currentValue)
      this.tableData = data
      this.updateChart(data);
    }
  }

  private createChart(data: DonutChartDatum[]) {
    this.processPieData(data);
    this.removeExistingChartFromParent();
    this.setChartDimensions();
    this.setColorScale();
    this.addGraphicsElement();
    this.setupArcGenerator();
    this.addSlicesToTheDonut();
    this.addLabelsToTheDonut();
    this.addDonutTotalLabel();
  }

  private processPieData(data, initial = true) {
    
    this.rawData = data;
    let count = 0
    data.forEach(element => {
      count+=element.count
    });
    this.total = count;

    this.pieData = this.pie(data.map(el => el.count));
    if (initial) {
      this.pieDataPrevious = this.pieData;
    }
  }

  public updateChart(data: DonutChartDatum[]) {
    if (!this.svg) {
      this.createChart(data);
      return;
    }
    this.processPieData(data, false);
    this.updateSlices();
    this.updateLabels();
  }

  private setChartDimensions() {
    let viewBoxHeight = 200;
    let viewBoxWidth = 200;
    this.svg = d3.select(this.hostElement).append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', '0 0 ' + viewBoxWidth + ' ' + viewBoxHeight)
      .style('background-color', '#f0f0f0');
  }

  private setColorScale() {
    this.colorScale = d3.scaleOrdinal(d3.schemeCategory10);
    // Below is an example of using custom colors
    // this.colorScale = d3.scaleOrdinal().domain(["0","1","2","3"]).range(['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728']);
  }

  private addGraphicsElement() {
    this.g = this.svg.append("g")
      .attr("transform", "translate(100,90)");
  }

  private setupArcGenerator() {
    this.innerRadius = 50;
    this.radius = 80;
    this.arc = d3.arc()
      .innerRadius(this.innerRadius)
      .outerRadius(this.radius);
  }

  private addSlicesToTheDonut() {
    this.slices = this.g.selectAll('allSlices')
      .data(this.pieData)
      .enter()
      .append('path')
      .attr('d', this.arc)
      .attr('fill', (datum, index) => {
        this.filterService.setChartColors( this.tableData[index], this.colorScale(`${index}`))
          return this.colorScale(`${index}`);
      })
      .style('opacity', 1)
      .on('click', (datum, index) => {
        this.clickedSlice.emit(this.tableData[index.index])
      })
  }

  private addDonutTotalLabel() {
    this.totalLabel = this.svg
      .append('text')
      .text(this.total)
      .attr('id', 'total')
      .attr('x', 100)
      .attr('y', 95)
      .style('font-size', '8px')
      .style('text-anchor', 'middle')
      // .style('cursor', 'pointer')
      .attr("class", "bar-text")
      // .on('click', (datum, index) => {
      // })
  }


  private updateSlices() {
    this.slices = this.slices.data(this.pieData);
    this.slices.transition().duration(750).attrTween("d", this.arcTween);
  }

  private updateLabels() {
    this.totalLabel.text(this.total);
    this.labels.data(this.pieData);
    this.labels.each((datum, index, n) => {
      d3.select(n[index]).text(this.labelValueFn(this.rawData[index]));
    });
    this.labels.transition().duration(750).attrTween("transform", this.labelTween);
  }

  private updateTotal() {
    this.totalLabel.text(this.total);
  }

  private removeExistingChartFromParent() {
    // !!!!Caution!!!
    // Make sure not to do;
    //     d3.select('svg').remove();
    // That will clear all other SVG elements in the DOM
    d3.select(this.hostElement).select('svg').remove();
  }

  private addLabelsToTheDonut() {
    this.labels = this.g
      .selectAll('allLabels')
      .data(this.pieData)
      .enter()
      .append('text')
      .text(this.labelValueGetter)
      .attr('transform', (datum, index) => {
          return 'translate(' + this.arc.centroid(datum) + ')';
      })
      .attr("class", "bar-text")
      .style('font-size', '5px')
      .style('text-anchor', 'middle')
      .style('fill', 'black')
  } 

  private labelValueGetter = (datum, index) => {
      return `${this.rawData[index].species} ${this.labelValueFn(this.rawData[index].count)}`;
  }

  private labelValueFn(val) {
      const pct = Math.floor(val * 100 / this.total);
      return (pct < 4) ? '' : '' + val;
  }


  // Creates an "interpolator" for animated transition for arc slices
    //   given previous and new arc shapes,
    //   generates a series of arc shapes (be)tween start and end state
    arcTween = (datum, index) => {
      const interpolation = d3.interpolate(this.pieDataPrevious[index], datum);
      this.pieDataPrevious[index] = interpolation(0);
      return (t) => {
        return this.arc(interpolation(t));
      }
  }

  // Creates an "interpolator" for animated transition for arc labels
  //   given previous and new label positions,
  //   generates a series of arc states (be)tween start and end state
  labelTween = (datum, index) => {
    const interpolation = d3.interpolate(this.pieDataPrevious[index], datum);
    this.pieDataPrevious[index] = interpolation(0);
    return (t) => {
      return 'translate(' + this.arc.centroid(interpolation(t)) + ')';
    }
  }
}
